import { Dialog, Box, Button, DialogContent, DialogActions, DialogContentText} from "@mui/material"
import { WorkspacePremium } from "@mui/icons-material"
import { useState } from "react"
import { useGetIdentity, useNotify, usePermissions, useRedirect, useTranslate } from "react-admin"
import { useMutation } from "react-query";
import { apiService } from "../../apiService";
import { BookInvoiceCommand, InvoiceReadModel, InvoiceType } from "../../GeneratedClients/FreeBillApi";
import CarSelect from "../../cars/CarSelect";
import validateBookingPeriod from "./validateBookingPeriod";
import validateInvoiceItems from "./validateInvoiceItems";
import { buildBookInvoiceCommand } from "./bookInvoiceHelpers";
import { AxiosError } from "axios";
import CustomCommandProgress from "../../customStyles/CustomCommandProgress";
import { Permissions } from "../../authentication/Permissions";
import UpgradeDialog from "../../authorization/UpgradeDialog";

interface IProps {
  invoice: InvoiceReadModel,
}

const CreateBookingTransactionDialog = ({invoice}: IProps) => {
  const translate = useTranslate()
  const redirect = useRedirect()
  const { isLoading: isLoadingPermissions, permissions } = usePermissions<Permissions>()
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity()
  const [open, setOpen] = useState<boolean>(false)
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState<boolean>(false)
  const [isValidBookingPeriod, setIsValidBookingPeriod] = useState<boolean>()
  const [isValidInvoiceItems, setIsValidInvoiceItems] = useState<boolean>()
  const bookingPeriod = localStorage.getItem("bookingPeriod")
  const notify = useNotify()
  const { mutate: bookInvoice, isLoading: isBooking } = useMutation((command: BookInvoiceCommand) => 
    apiService.InvoicesApi.bookInvoice({id: invoice.id!, bookInvoiceCommand: command}),
    {
      onSuccess: () => {
        notify(translate("resources.invoices.notifications.booked"))
        redirect('list', "invoices")
      },
      onError: (error: AxiosError) => {
        console.log(error)
        notify(`${translate("resources.invoices.notifications.bookingDenied")} ${error.response?.data ? `${error.response.data}` : ''}`, {type: "error", autoHideDuration: 8000})
      }
    }
  )

  if(isLoadingPermissions || isLoadingIdentity || !identity) return null

  const handleOpen = () => {
    if(invoice.bookingInfos !== null)
    {
      notify(translate(`resources.invoices.dialogs.alreadyBooked`))
      return
    }
    if(permissions.fffUser)
    {
      setOpen(true)
      var isValidBookingPeriod = invoice.invoiceDate ? validateBookingPeriod(new Date(invoice.invoiceDate)) : undefined
      var isValidInvoiceItems = invoice.invoiceItems ? validateInvoiceItems(invoice.invoiceItems) : undefined
      setIsValidBookingPeriod(isValidBookingPeriod)
      setIsValidInvoiceItems(isValidInvoiceItems)
    } else {
      setOpenUpgradeDialog(true)
    }
  
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleBook = () => {
    bookInvoice(buildBookInvoiceCommand(invoice))
  }

  return (
    <>
      <Button 
        size='small' 
        onClick={handleOpen}
        
      >
        {translate('resources.invoices.actions.book')}
        {
          !permissions.fffUser && <WorkspacePremium sx={{ml:0.5}} fontSize={"small"} color={"action"} />
        }
      </Button>
      <Dialog 
        open={open} 
        onClose={handleClose}
      >
        <DialogContent>
          <DialogContentText>
            {
              isValidBookingPeriod && isValidInvoiceItems && translate(`resources.invoices.dialogs.book`)
            }
            {
              !isValidBookingPeriod && translate(`resources.invoices.dialogs.invalidPeriod`)
            }
            {
              !isValidInvoiceItems && translate(`resources.invoices.dialogs.invalidItems`)
            }
            {
              isValidInvoiceItems && isValidBookingPeriod && invoice.invoiceType === InvoiceType.SaleCarInvoice &&
              <CarSelect />
            }
          </DialogContentText>
          {
            bookingPeriod &&
            <DialogContentText>
              {translate(`resources.invoices.bookingPeriod`)}: <strong>{bookingPeriod}</strong>
            </DialogContentText>
          }
          { isBooking && <Box mt={2}><CustomCommandProgress /></Box> }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} >{translate('ra.action.cancel')}</Button>
          {
            isValidInvoiceItems && isValidBookingPeriod && 
            <Button onClick={handleBook} >{translate('resources.invoices.actions.book')}</Button>
          }
        </DialogActions>
      </Dialog>
      <UpgradeDialog 
        open={openUpgradeDialog}
        setOpen={setOpenUpgradeDialog}
        title={translate("resources.authorization.upgradeDialog.title")}
        content={translate("resources.authorization.upgradeDialog.description")}
      />
    </>
  )
}

export default CreateBookingTransactionDialog